import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Save,
  ShoppingBag,
  Unplug,
} from 'lucide-react';
import { authApi, metaShopsApi } from '@/services/api';
import { META_SHOPS_SCOPES } from '@/components/settings/setupGuides';

/**
 * Meta Shops (Facebook / Instagram) configure panel — Commerce API credentials + how-to guide.
 * Separate from WhatsApp Shop under Marketplaces → WhatsApp.
 */
export default function MetaShopsIntegrationTab() {
  const qc = useQueryClient();
  const [showSecret, setShowSecret] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [guideOpen, setGuideOpen] = useState(true);
  const [copied, setCopied] = useState<'scopes' | null>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const features: string[] = Array.isArray(profile?.features) ? profile.features : [];
  const packageAllows =
    Boolean(profile?.allow_meta) ||
    features.includes('allow_meta') ||
    (Array.isArray(profile?.selected_add_ons) &&
      profile.selected_add_ons.some(
        (a: string | { status?: string; feature_key?: string; addon_code?: string }) => {
          if (typeof a === 'string') return a === 'allow_meta';
          if (a?.status && a.status !== 'Active') return false;
          return a?.feature_key === 'allow_meta' || a?.addon_code === 'allow_meta';
        },
      ));

  const { data: status, refetch } = useQuery({
    queryKey: ['metaShopsStatus'],
    queryFn: async () => (await metaShopsApi.getStatus()).data.message,
    enabled: packageAllows,
  });

  const [form, setForm] = useState({
    enable_meta: false,
    meta_commerce_account_id: '',
    meta_catalog_id: '',
    meta_page_id: '',
    meta_business_id: '',
    meta_app_id: '',
    meta_app_secret: '',
    meta_access_token: '',
    meta_sync_products_mode: 'Selected',
    meta_auto_confirm_orders: false,
    meta_auto_fetch_orders: false,
  });

  useEffect(() => {
    if (!profile) return;
    setForm((f) => ({
      ...f,
      enable_meta: Boolean(profile.enable_meta),
      meta_commerce_account_id: profile.meta_commerce_account_id || '',
      meta_catalog_id: profile.meta_catalog_id || '',
      meta_page_id: profile.meta_page_id || '',
      meta_business_id: profile.meta_business_id || '',
      meta_app_id: profile.meta_app_id || '',
      meta_sync_products_mode: profile.meta_sync_products_mode || 'Selected',
      meta_auto_confirm_orders: Boolean(profile.meta_auto_confirm_orders),
      meta_auto_fetch_orders: Boolean(profile.meta_auto_fetch_orders),
    }));
  }, [profile]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...form };
      if (!form.meta_app_secret) delete payload.meta_app_secret;
      if (!form.meta_access_token) delete payload.meta_access_token;
      await authApi.updateCompanyProfile(payload);
    },
    onSuccess: () => {
      toast.success('Meta Shops settings saved');
      qc.invalidateQueries({ queryKey: ['profile'] });
      void refetch();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Save failed'),
  });

  const run = async (fn: () => Promise<any>, okText: string) => {
    setMsg(null);
    try {
      const res = await fn();
      const text = typeof res === 'string' ? res : res?.message || okText;
      setMsg({ ok: true, text: typeof text === 'string' ? text : okText });
      void refetch();
      qc.invalidateQueries({ queryKey: ['profile'] });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.message || e.message || 'Failed' });
    }
  };

  const scopesCsv = META_SHOPS_SCOPES.join(',');
  const copyScopes = async () => {
    try {
      await navigator.clipboard.writeText(scopesCsv);
      setCopied('scopes');
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!packageAllows) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 space-y-3 max-w-xl">
        <p className="font-medium">Meta Shops add-on required</p>
        <p>Contact Support to purchase the Meta Shops integration for your company.</p>
        <Link
          to="/support?subject=Enable%20Meta%20Shops%20integration"
          className="btn-primary text-sm w-fit inline-flex"
        >
          Contact Support
        </Link>
      </div>
    );
  }

  const connected = Boolean(status?.connected);

  return (
    <div className="space-y-5 max-w-3xl min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Meta Shops</h2>
            <p className="text-sm text-gray-500">Facebook &amp; Instagram Shops via Meta Commerce API</p>
          </div>
        </div>
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            connected ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {connected ? 'Connected' : status?.connection_status || 'Disconnected'}
        </span>
      </div>

      {msg && (
        <div
          className={`rounded-lg border px-3 py-2 text-sm flex gap-2 ${
            msg.ok ? 'border-emerald-100 bg-emerald-50 text-emerald-900' : 'border-red-100 bg-red-50 text-red-800'
          }`}
        >
          {msg.ok ? (
            <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          )}
          <span>{msg.text}</span>
        </div>
      )}

      {/* How to get credentials from Meta */}
      <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-white overflow-hidden min-w-0">
        <button
          type="button"
          onClick={() => setGuideOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-w-0"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-indigo-950 min-w-0">
            <BookOpen className="w-4 h-4 shrink-0" />
            <span className="truncate">How to get credentials from Meta</span>
          </span>
          {guideOpen ? (
            <ChevronUp className="w-4 h-4 text-indigo-800 shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-indigo-800 shrink-0" />
          )}
        </button>

        {guideOpen && (
          <div className="px-4 pb-4 space-y-4 text-sm text-gray-700 min-w-0 overflow-x-hidden">
            <p className="text-gray-600 break-words">
              Collect App ID, App Secret, system-user Access Token, Catalog ID and Commerce Merchant Settings
              (CMS) ID from Meta — then paste them below. This is separate from{' '}
              <strong>WhatsApp Shop</strong> under Marketplaces → WhatsApp.
            </p>

            <div className="rounded-lg border border-indigo-200 bg-white px-3 py-2.5 text-xs text-indigo-950 min-w-0">
              <p className="font-semibold mb-1">Path to the form</p>
              <p className="font-mono text-[12px] tracking-tight leading-relaxed break-words whitespace-normal">
                <a
                  href="https://developers.facebook.com/apps/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-indigo-600/40 font-bold"
                >
                  developers.facebook.com
                </a>
                {' '}→ My Apps → Create App{' · '}
                Business Suite → System users → Generate Token{' · '}
                Commerce Manager → Catalog
              </p>
              <p className="mt-1.5 text-gray-600 break-words">
                Docs:{' '}
                <a
                  href="https://developers.facebook.com/docs/commerce-platform/api-setup/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-800 underline break-all"
                >
                  Commerce API setup
                </a>
              </p>
            </div>

            <p className="text-sm font-semibold text-gray-900">On Meta for Developers / Business Suite</p>
            <ol className="space-y-3 list-decimal list-inside">
              <li>
                <span className="font-medium text-gray-900">Create a developer app</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Open <strong>My Apps → Create App</strong> (Business type). Add the app to your Meta Business
                  Suite. You must be a Business admin.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">App name:</span>{' '}
                    <code className="font-semibold text-gray-900">SaleDesk POS</code>
                  </p>
                  <p>
                    <span className="text-gray-500">App type:</span>{' '}
                    <span className="font-medium">Business</span>
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Commerce Manager shop</span>
                <p className="mt-1 ml-5 text-gray-600">
                  In <strong>Commerce Manager</strong>, create or open your Facebook / Instagram Shop, finish
                  commerce eligibility checks, and link a product catalog.
                </p>
              </li>

              <li>
                <span className="font-medium text-gray-900">System user + assign assets</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Business Suite → <strong>Users → System users</strong>. Create a system user and assign{' '}
                  <strong>admin</strong> access to: your app, Facebook Page, catalog, and commerce (CMS) account.
                </p>
              </li>

              <li>
                <span className="font-medium text-gray-900">Generate Access Token</span>
                <p className="mt-1 ml-5 text-gray-600">
                  On the system user, click <strong>Generate Token</strong> for your app. Request these
                  permissions:
                </p>
                <div className="mt-2 ml-5 flex flex-wrap gap-1.5">
                  {META_SHOPS_SCOPES.map((s) => (
                    <code
                      key={s}
                      className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-indigo-100 text-indigo-900"
                    >
                      {s}
                    </code>
                  ))}
                </div>
                <div className="mt-2 ml-5 flex flex-col sm:flex-row gap-2 min-w-0">
                  <code className="min-w-0 flex-1 text-[11px] bg-white border border-indigo-100 rounded px-2 py-1.5 break-all whitespace-pre-wrap">
                    {scopesCsv}
                  </code>
                  <button
                    type="button"
                    onClick={() => void copyScopes()}
                    className="btn-secondary px-2 py-1.5 text-xs inline-flex items-center justify-center gap-1 shrink-0"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy scopes
                  </button>
                </div>
                {copied === 'scopes' && <p className="ml-5 text-xs text-emerald-600 mt-1">Copied</p>}
                <div className="mt-2 ml-5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-950 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    Prefer an expiring (60-day) system-user token. Before App Review, Meta may only allow test
                    commerce accounts.
                  </span>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Copy Catalog ID &amp; CMS ID</span>
                <p className="mt-1 ml-5 text-gray-600">
                  In Commerce Manager, open the catalog — Catalog ID appears in the URL or catalog settings.
                  Commerce Merchant Settings (CMS) / commerce account ID is on the shop settings.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">Catalog ID →</span> Catalog ID
                  </p>
                  <p>
                    <span className="text-gray-500">CMS / Commerce account ID →</span> Commerce Account ID
                  </p>
                  <p>
                    <span className="text-gray-500">Page ID →</span> Facebook Page ID
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">App ID &amp; App Secret</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Meta for Developers → your app → <strong>Settings → Basic</strong>. Copy App ID and App Secret.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 text-xs text-emerald-950 space-y-2">
                  <p className="font-semibold">Paste into SaleDesk (below)</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-gray-800">
                    <li>App ID → <strong>App ID</strong></li>
                    <li>App Secret → <strong>App Secret</strong></li>
                    <li>System-user token → <strong>Access Token</strong></li>
                    <li>Catalog / CMS / Page IDs into matching fields</li>
                    <li>Save, then <strong>Test connection</strong></li>
                  </ol>
                  <p className="text-gray-600 border-t border-emerald-200/80 pt-2">
                    Never share App Secret or Access Token in chat.
                  </p>
                </div>
              </li>
            </ol>

            <div className="rounded-lg bg-white/80 border border-indigo-100 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">Tips</p>
              <p>• Keep SaleDesk SKUs aligned with Meta retailer IDs / catalog items.</p>
              <p>• WhatsApp Shop uses different Marketplaces credentials — do not mix them unless intentional.</p>
              <p>
                • Official setup:{' '}
                <a
                  href="https://developers.facebook.com/docs/commerce-platform/api-setup/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-800 underline"
                >
                  Commerce API Integration Setup
                </a>
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={form.enable_meta}
            onChange={(e) => setForm({ ...form, enable_meta: e.target.checked })}
          />
          Enable Meta Shops integration
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Commerce Account ID (CMS)</label>
            <input
              className="input-field mt-1"
              value={form.meta_commerce_account_id}
              onChange={(e) => setForm({ ...form, meta_commerce_account_id: e.target.value })}
              placeholder="Commerce Merchant Settings ID"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Catalog ID</label>
            <input
              className="input-field mt-1"
              value={form.meta_catalog_id}
              onChange={(e) => setForm({ ...form, meta_catalog_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Facebook Page ID</label>
            <input
              className="input-field mt-1"
              value={form.meta_page_id}
              onChange={(e) => setForm({ ...form, meta_page_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Business Manager ID</label>
            <input
              className="input-field mt-1"
              value={form.meta_business_id}
              onChange={(e) => setForm({ ...form, meta_business_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Product sync mode</label>
            <select
              className="input-field mt-1"
              value={form.meta_sync_products_mode}
              onChange={(e) => setForm({ ...form, meta_sync_products_mode: e.target.value })}
            >
              <option value="Selected">Selected (Show on Meta)</option>
              <option value="All active">All active</option>
              <option value="Manual">Manual only</option>
            </select>
          </div>
          <div className="flex flex-col gap-2 justify-end pb-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.meta_auto_fetch_orders}
                onChange={(e) => setForm({ ...form, meta_auto_fetch_orders: e.target.checked })}
              />
              Auto-fetch orders
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.meta_auto_confirm_orders}
                onChange={(e) => setForm({ ...form, meta_auto_confirm_orders: e.target.checked })}
              />
              Auto-confirm mapped orders
            </label>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <p className="text-sm font-medium text-gray-800">App &amp; access token</p>
          <p className="text-xs text-gray-500">
            From Meta for Developers → Settings → Basic, and Business Suite → System users → Generate Token.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">App ID</label>
              <input
                className="input-field mt-1"
                value={form.meta_app_id}
                onChange={(e) => setForm({ ...form, meta_app_id: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">
                App Secret {profile?.has_meta_app_secret ? '(saved)' : ''}
              </label>
              <div className="relative mt-1">
                <input
                  className="input-field pr-10"
                  type={showSecret ? 'text' : 'password'}
                  value={form.meta_app_secret}
                  placeholder={profile?.has_meta_app_secret ? '••••••••' : ''}
                  onChange={(e) => setForm({ ...form, meta_app_secret: e.target.value })}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowSecret((v) => !v)}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-gray-600">
                Access Token {profile?.has_meta_access_token ? '(saved)' : ''}
              </label>
              <div className="relative mt-1">
                <input
                  className="input-field pr-10"
                  type={showToken ? 'text' : 'password'}
                  value={form.meta_access_token}
                  placeholder={profile?.has_meta_access_token ? '••••••••' : 'System-user token'}
                  onChange={(e) => setForm({ ...form, meta_access_token: e.target.value })}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowToken((v) => !v)}
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() =>
              void run(async () => {
                const r = await metaShopsApi.testConnection();
                return r.data.message;
              }, 'Connection OK')
            }
          >
            Test connection
          </button>
          <button
            type="button"
            className="btn-secondary text-sm inline-flex items-center gap-1.5 text-red-700"
            onClick={() =>
              void run(async () => {
                await metaShopsApi.disconnect();
                setForm((f) => ({
                  ...f,
                  meta_app_secret: '',
                  meta_access_token: '',
                }));
                return { message: 'Disconnected' };
              }, 'Disconnected')
            }
          >
            <Unplug className="w-3.5 h-3.5" />
            Disconnect
          </button>
          <Link to="/channel-orders?channel=meta" className="btn-secondary text-sm w-fit">
            Marketplace Orders
          </Link>
        </div>

        <p className="text-xs text-gray-500">
          Catalogue / order sync will use these Commerce credentials. Confirm Test connection succeeds before
          enabling auto-fetch.
        </p>
      </div>
    </div>
  );
}
